import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Card,
  Form,
  InputGroup,
  Button,
  Spinner,
} from "@themesberg/react-bootstrap";
import {
  FiPlus,
  FiSearch,
  FiUsers,
  FiCheckCircle,
  FiTrendingUp,
  FiAward,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { userMySubIdsList, userToggleSubIdStatus } from "../../../api";
import IdCard, { formatCurrency } from "./IdCard";
import AddIdModal from "./AddIdModal";
import IdDetailDrawer from "./IdDetailDrawer";
import "./myIds.css";

const getAuthUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("authUser") || "{}");
  } catch {
    return {};
  }
};

const EmptyStateIcon = () => (
  <svg
    className="my-ids-empty-icon"
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="22" cy="20" r="8" stroke="currentColor" strokeWidth="2" />
    <path
      d="M10 48c0-6.627 5.373-12 12-12s12 5.373 12 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="44" cy="24" r="7" stroke="currentColor" strokeWidth="2" />
    <path
      d="M34 48c0-5.523 4.477-10 10-10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M28 32l8 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export default function MyIds() {
  const authUser = getAuthUser();
  const isSubIdAccount = authUser?.is_sub_id === true;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [ids, setIds] = useState([]);
  const [summary, setSummary] = useState({
    total_ids: 0,
    active_ids: 0,
    total_earnings_all_ids: 0,
    ids_earning_commission: 0,
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerTab, setDrawerTab] = useState("overview");
  const [showDrawer, setShowDrawer] = useState(false);

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchIds = async (page = 1, searchTerm = "", status = "") => {
    try {
      setLoading(true);
      const response = await userMySubIdsList({
        page,
        per_page: 12,
        search: searchTerm || undefined,
        status: status || undefined,
      });

      const data = response?.data?.data || {};
      setIds(Array.isArray(data.ids) ? data.ids : []);
      setSummary(
        data.summary || {
          total_ids: 0,
          active_ids: 0,
          total_earnings_all_ids: 0,
          ids_earning_commission: 0,
        }
      );
      setPagination(
        data.pagination || {
          current_page: page,
          last_page: 1,
          per_page: 12,
          total: 0,
        }
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to fetch My IDs");
      setIds([]);
      setSummary({
        total_ids: 0,
        active_ids: 0,
        total_earnings_all_ids: 0,
        ids_earning_commission: 0,
      });
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 12,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSubIdAccount) {
      fetchIds(1, "", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubIdAccount]);

  useEffect(() => {
    if (isSubIdAccount || firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchIds(1, search.trim(), statusFilter);
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (isSubIdAccount || firstSearchRenderRef.current) return;
    fetchIds(1, search.trim(), statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleView = (item) => {
    setDrawerItem(item);
    setDrawerTab("overview");
    setShowDrawer(true);
  };

  const handleManageAction = async (action, item) => {
    if (action === "view") {
      handleView(item);
      return;
    }

    if (action === "password" || action === "bank") {
      setDrawerItem(item);
      setDrawerTab("settings");
      setShowDrawer(true);
      return;
    }

    if (action === "toggle") {
      const isActive = String(item.status).toLowerCase() === "active";
      const verb = isActive ? "deactivate" : "activate";
      if (!window.confirm(`Are you sure you want to ${verb} ${item.name}?`)) return;

      try {
        const response = await userToggleSubIdStatus(item.custom_user_id);
        toast.success(response?.data?.message || "Status updated");
        fetchIds(pagination.current_page, search.trim(), statusFilter);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to update status");
      }
    }
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (current <= 2) return [1, 2, 3, "...", totalPages];
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  if (isSubIdAccount) {
    return (
      <Card className="border-0 shadow-sm rounded-4 mt-4">
        <Card.Body className="p-5 text-center">
          <h4 className="mb-2">My IDs</h4>
          <p className="text-muted mb-0">
            Sub-ID accounts cannot manage other IDs. Please log in with your primary account.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="my-ids-page py-4">
      <div className="my-ids-header d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="h4 mb-1">My IDs</h2>
          <p className="text-muted mb-0 small">
            Manage your sub-accounts and track their income
          </p>
        </div>
        <Button
          className="my-ids-btn-primary d-inline-flex align-items-center gap-2"
          onClick={() => setShowAddModal(true)}
        >
          <FiPlus /> Add ID
        </Button>
      </div>

      <div className="my-ids-summary-grid mb-4">
        <div className="my-ids-summary-card">
          <div className="my-ids-summary-icon navy">
            <FiUsers size={20} />
          </div>
          <div>
            <div className="text-muted small">Total IDs</div>
            <div className="fw-bold fs-5">{summary.total_ids ?? 0}</div>
          </div>
        </div>
        <div className="my-ids-summary-card">
          <div className="my-ids-summary-icon green">
            <FiCheckCircle size={20} />
          </div>
          <div>
            <div className="text-muted small">Active</div>
            <div className="fw-bold fs-5">{summary.active_ids ?? 0}</div>
          </div>
        </div>
        <div className="my-ids-summary-card">
          <div className="my-ids-summary-icon navy">
            <FiTrendingUp size={20} />
          </div>
          <div>
            <div className="text-muted small">Total Earnings</div>
            <div className="fw-bold fs-5">
              {formatCurrency(summary.total_earnings_all_ids)}
            </div>
          </div>
        </div>
        <div className="my-ids-summary-card">
          <div className="my-ids-summary-icon amber">
            <FiAward size={20} />
          </div>
          <div>
            <div className="text-muted small">IDs Earning Commission</div>
            <div className="fw-bold fs-5">{summary.ids_earning_commission ?? 0}</div>
          </div>
        </div>
      </div>

      <div className="my-ids-filter-bar mb-4">
        <div className="row g-2 align-items-center">
          <div className="col-md-8">
            <InputGroup>
              <InputGroup.Text className="bg-white">
                <FiSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name, ID, username, or referral"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ boxShadow: "none" }}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading IDs...
        </div>
      ) : ids.length > 0 ? (
        <>
          <div className="my-ids-card-grid mb-4">
            {ids.map((item) => (
              <IdCard
                key={item.custom_user_id || item.id}
                item={item}
                onView={handleView}
                onManageAction={handleManageAction}
              />
            ))}
          </div>

          {pagination.last_page > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap my-ids-pagination">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={pagination.current_page <= 1}
                onClick={() =>
                  fetchIds(
                    Math.max(1, pagination.current_page - 1),
                    search.trim(),
                    statusFilter
                  )
                }
              >
                <FiChevronLeft />
              </Button>

              {getPageItems().map((pageItem, index) =>
                pageItem === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={pageItem}
                    variant="outline-primary"
                    size="sm"
                    className={pageItem === pagination.current_page ? "btn-active" : ""}
                    onClick={() => fetchIds(pageItem, search.trim(), statusFilter)}
                  >
                    {pageItem}
                  </Button>
                )
              )}

              <Button
                variant="outline-primary"
                size="sm"
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() =>
                  fetchIds(
                    Math.min(pagination.last_page, pagination.current_page + 1),
                    search.trim(),
                    statusFilter
                  )
                }
              >
                <FiChevronRight />
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="my-ids-empty-state">
          <EmptyStateIcon />
          <h5 className="fw-bold mb-2">No IDs yet</h5>
          <p className="text-muted mb-4">
            Create your first sub-account to expand your network.
          </p>
          <Button
            className="my-ids-btn-primary d-inline-flex align-items-center gap-2"
            onClick={() => setShowAddModal(true)}
          >
            <FiPlus /> Add ID
          </Button>
        </div>
      )}

      <AddIdModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onCreated={() => fetchIds(pagination.current_page, search.trim(), statusFilter)}
      />

      <IdDetailDrawer
        show={showDrawer}
        item={drawerItem}
        initialTab={drawerTab}
        ownerCustomUserId={authUser?.custom_user_id}
        onClose={() => {
          setShowDrawer(false);
          setDrawerItem(null);
        }}
        onUpdated={() => fetchIds(pagination.current_page, search.trim(), statusFilter)}
      />
    </div>
  );
}
